import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Bg = styled.div`
  background: var(--color-bg);
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', 'Noto Sans', sans-serif;
`
const Container = styled.div`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 9rem 1.5rem 5rem;

  @media (min-width: 1440px) {
    padding: 10rem 2rem 6rem;
  }
  @media (max-width: 768px) {
    padding: 8rem 1.25rem 3rem;
  }
`
const Title = styled.h1`
  color: var(--color-ink);
  font-family: 'Playfair Display', 'Noto Serif TC', serif;
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
  line-height: 1.15;
`
const Desc = styled.p`
  color: var(--color-muted);
  font-size: 1.05rem;
  line-height: 1.75;
  margin-bottom: 2.5rem;
  max-width: 680px;
`
const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1px solid var(--color-line);
  padding: 1.25rem 1.75rem;
  min-height: 80px;
  border-radius: var(--radius-lg);
  margin-bottom: 0.75rem;
  box-shadow: var(--shadow-card);
`
const InfoIcon = styled.div`
  background: var(--color-bg-soft);
  color: var(--color-primary);
  border-radius: var(--radius-sm);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`
const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`
const InfoLabel = styled.p`
  color: var(--color-ink);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
`
const InfoValue = styled.p`
  color: var(--color-ink);
  font-size: 1rem;
  font-weight: 500;
`
const FormTitle = styled.h2`
  color: var(--color-ink);
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 3rem 0 1.25rem 0;
`
const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-card);

  @media (min-width: 1440px) {
    gap: 2.5rem;
    padding: 3rem;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.75rem;
    border-radius: var(--radius-lg);
  }
`
const Form = styled.form`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const FormImage = styled.div`
  min-height: 320px;
  background: #f3e7eb;
  background-image: url('/images/contact.jpg');
  background-size: cover;
  background-position: center;
  border-radius: var(--radius-lg);
  @media (max-width: 768px) {
    min-height: 180px;
    height: 40vw;
    max-height: 240px;
  }
`
const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
const Input = styled.input`
  border: 1px solid var(--color-line);
  background: var(--color-bg);
  border-radius: var(--radius-sm);
  padding: 0.95rem 1.1rem;
  color: var(--color-ink);
  font-size: 1rem;
  transition: border-color 0.25s ease, background 0.25s ease;
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: #fff;
  }
`
const Select = styled.select`
  border: 1px solid var(--color-line);
  background: var(--color-bg);
  border-radius: var(--radius-sm);
  padding: 0.95rem 1.1rem;
  color: var(--color-ink);
  font-size: 1rem;
  transition: border-color 0.25s ease, background 0.25s ease;
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: #fff;
  }
`
const Textarea = styled.textarea`
  border: 1px solid var(--color-line);
  background: var(--color-bg);
  border-radius: var(--radius-sm);
  padding: 0.95rem 1.1rem;
  color: var(--color-ink);
  font-size: 1rem;
  font-family: inherit;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.25s ease, background 0.25s ease;
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: #fff;
  }
`
const Spinner = styled.div`
  border: 3px solid #f3e7eb;
  border-top: 3px solid #ef427c;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
const SubmitBtn = styled.button`
  background: var(--color-ink);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.95rem 2rem;
  cursor: pointer;
  margin-top: 0.75rem;
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.02em;
  transition: all 0.25s ease;

  &:hover {
    background: var(--color-primary);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`
const ErrorMessage = styled.p`
  color: #ef427c;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`

const Contact = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name) newErrors.name = '請輸入姓名'
    if (!formData.email) newErrors.email = '請輸入電子郵件'
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = '請輸入有效的電子郵件'
    if (!formData.phone) newErrors.phone = '請輸入電話'
    if (!formData.service) newErrors.service = '請選擇服務項目'
    if (formData.message && formData.message.length > 500)
      newErrors.message = '留言不能超過 500 字'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (errors[name]) setErrors({ ...errors, [name]: '' })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    const formUrl =
      'https://docs.google.com/forms/d/e/1FAIpQLSfHdi8Gonyhh4KddGHDY_nA86Lhx2xQ1wWwC2LRww3HZ6vfLw/formResponse'

    const form = new FormData()
    form.append('entry.651906979', formData.name)
    form.append('entry.1145700178', formData.email)
    form.append('entry.2066705531', formData.phone)
    form.append('entry.1398000762', formData.service)
    if (formData.message) form.append('entry.1398000763', formData.message)

    fetch(formUrl, {
      method: 'POST',
      mode: 'no-cors',
      body: form
    })
      .then(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        })
        setIsSubmitting(false)
        navigate('/thank-you')
      })
      .catch(() => {
        setIsSubmitting(false)
        alert('送出失敗，請稍後再試')
      })
  }

  return (
    <Bg>
      <Container>
        <div style={{ marginBottom: '2rem' }}>
          <Title>聯絡雲耘科技</Title>
          <Desc>
            無論您是考慮導入 Beautefeel
            的美業店家、希望深入交流產業趨勢的媒體夥伴，或正在尋找合作機會的企業，
            歡迎透過以下方式與我們聯繫，或填寫下方諮詢表單，我們將於 1 個工作日內回覆。
          </Desc>
        </div>
        <h3
          style={{
            color: '#d5728a',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            margin: '0 0 1rem 0'
          }}
        >
          Contact Info
        </h3>
        <InfoBlock>
          <InfoIcon>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='currentColor'
              viewBox='0 0 256 256'
            >
              <path d='M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85,12,11.05a8,8,0,0,0,10.82,0l12-11.05,58,53.15H52.57ZM157.29,128,216,74.18V181.82Z' />
            </svg>
          </InfoIcon>
          <InfoText>
            <InfoLabel>電子郵件</InfoLabel>
            <InfoValue>service@beautefeel.com</InfoValue>
          </InfoText>
        </InfoBlock>
        <InfoBlock>
          <InfoIcon>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='currentColor'
              viewBox='0 0 256 256'
            >
              <path d='M222,158.4l-46.9-21a16,16,0,0,0-15.2,1.4,1.8,1.8,0,0,0-.5.4l-24.5,20.8a3.9,3.9,0,0,1-3.6.2c-15.4-7.4-31.3-23.2-38.8-38.4a3.8,3.8,0,0,1,.2-3.6L113.6,93.6l.4-.5a16,16,0,0,0,1.3-15.1L94.4,31.2A16,16,0,0,0,77.8,21.7,56.3,56.3,0,0,0,32,77.6C32,161.2,94.8,224,178.4,224a56.3,56.3,0,0,0,55.9-45.8A16,16,0,0,0,222,158.4Z' />
            </svg>
          </InfoIcon>
          <InfoText>
            <InfoLabel>聯絡電話</InfoLabel>
            <InfoValue>
              <a
                href='tel:+886962023638'
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                0962-023-638
              </a>
            </InfoValue>
          </InfoText>
        </InfoBlock>
        <FormTitle>諮詢表單</FormTitle>
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <Label>
              <span>姓名</span>
              <Input
                name='name'
                placeholder='請輸入您的姓名'
                onChange={handleChange}
                value={formData.name}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Label>
            <Label>
              <span>電子郵件</span>
              <Input
                name='email'
                type='email'
                placeholder='請輸入您的電子郵件'
                onChange={handleChange}
                value={formData.email}
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </Label>
            <Label>
              <span>電話</span>
              <Input
                name='phone'
                placeholder='請輸入您的電話'
                onChange={handleChange}
                value={formData.phone}
              />
              {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            </Label>
            <Label>
              <span>服務項目</span>
              <Select
                name='service'
                onChange={handleChange}
                value={formData.service}
              >
                <option value=''>請選擇諮詢項目</option>
                <option value='Beautefeel 導入諮詢'>Beautefeel 導入諮詢</option>
                <option value='Beautefeel 產品試用'>Beautefeel 產品試用</option>
                <option value='連鎖 / 企業客製方案'>連鎖 / 企業客製方案</option>
                <option value='媒體採訪 / 合作提案'>媒體採訪 / 合作提案</option>
                <option value='其他諮詢'>其他諮詢</option>
              </Select>
              {errors.service && <ErrorMessage>{errors.service}</ErrorMessage>}
            </Label>
            <Label>
              <span>留言</span>
              <Textarea
                name='message'
                placeholder='請簡述您的需求或專案內容'
                onChange={handleChange}
                value={formData.message}
              />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </Label>
            <SubmitBtn type='submit' disabled={isSubmitting}>
              {isSubmitting && <Spinner />}
              {isSubmitting ? '送出中...' : '送出'}
            </SubmitBtn>
          </Form>
          <FormImage />
        </FormContainer>
      </Container>
    </Bg>
  )
}

export default Contact
